// src/utils/resumeUploadAPI.js
import { API_ENDPOINTS } from "../config/api";
import { authFetch } from './authFetch';

const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

export const uploadResumePdf = async (userEmail, file) => {
    try {
        if (!file) throw new Error("No file provided");
        if (!file.name?.toLowerCase().endsWith(".pdf")) {
            throw new Error("Only PDF files are accepted");
        }

        // Optional front-end size check (5MB max after decoding)
        const MAX = 5 * 1024 * 1024;
        if (file.size > MAX) throw new Error("PDF must be 5MB or less");

        const dataUrl = await fileToBase64(file);
        // data:application/pdf;base64,XXXX
        const base64 = String(dataUrl).split(",")[1] || "";

        const payload = {
            user_id: userEmail,
            file_name: file.name,
            file_content: base64,
        };

        const res = await authFetch(API_ENDPOINTS.UPLOAD_RESUME, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });

        if (!res.ok) {
            const err = await res.json().catch(() => null);
            throw new Error(err?.body || "Failed to upload resume");
        }

        const data = await res.json();
        return { success: true, data };
    } catch (e) {
        return { success: false, message: e.message || "Upload failed" };
    }
};

export const getResumeMetadata = async (userEmail) => {
    try {
        const res = await authFetch(
            `${API_ENDPOINTS.GET_RESUME_METADATA}?user_id=${encodeURIComponent(userEmail)}`
        );
        if (!res.ok) {
            if (res.status === 404) {
                return { success: true, hasResume: false, data: null };
            }
            const err = await res.json().catch(() => null);
            throw new Error(err?.body || "Failed to fetch resume metadata");
        }

        const data = await res.json();
        return { success: true, hasResume: true, data };
    } catch (e) {
        return { success: false, hasResume: false, message: e.message };
    }
};

export const downloadResumeUrl = async (userEmail) => {
    try {
        const res = await authFetch(
            `${API_ENDPOINTS.DOWNLOAD_RESUME}?user_id=${encodeURIComponent(userEmail)}`
        );

        if (!res.ok) {
            if (res.status === 404) {
                return { success: true, hasResume: false };
            }
            const err = await res.json().catch(() => null);
            throw new Error(err?.body || "Failed to fetch download URL");
        }

        const data = await res.json();
        return {
            success: true,
            hasResume: true,
            downloadUrl: data.download_url,
            fileName: data.file_name,
            expiresIn: Number(data.expires_in || 300),
        };
    } catch (e) {
        return { success: false, hasResume: false, message: e.message };
    }
};

export const deleteUploadedResume = async (userEmail) => {
    try {
        const res = await authFetch(API_ENDPOINTS.DELETE_RESUME, {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ user_id: userEmail }),
        });

        if (!res.ok) {
            if (res.status === 404) {
                // Already not there ΓÇö treat as success
                return { success: true };
            }
            const err = await res.json().catch(() => null);
            throw new Error(err?.body || "Failed to delete uploaded resume");
        }

        return { success: true };
    } catch (e) {
        return { success: false, message: e.message || "Delete failed" };
    }
};
